/* perDgPayrollActuals — Build C (C-10). Derive the per-department historical payroll is_actuals keys
 * (sm_<dg>_<paytype>, and collapsed cogs_wages/ga_wages/rd_wages for single-dg categories) that
 * buildFinancialsPayload DROPS today (a Skip'd payroll row has no is_line_key → :867/869 `continue`).
 *
 * The hard trap: the key needs department_mappings (Step 5c), which arrive AFTER the Step-4 import —
 * so this is a BACK-FILL. A Skip'd payroll row carries gl_category = a DEPARTMENT NAME and
 * is_category = 'Skip' (unusable); BOTH cat_slug and display_group come from the department its
 * gl_category token-matches. Reuses the FOUR existing modules (no new matcher, D2):
 *   matchPayrollRole + resolvePayrollDept (inferIsMeta), buildPayrollIsLineKey (payrollKeys),
 *   IS_CATEGORY_DISPLAY_TO_SLUG (lineRoles), matchISCategory (isCategoryMatch).
 *
 * ★ FAIL LOUD, NEVER GUESS (D4/D5): an unresolvable category, an unresolvable dg in a MULTI-dg
 * category, or two gl_categories colliding on one key → reported, not written. A wrong dg writes
 * history to the wrong leaf — C-10 in a different hat.
 *
 * PURE. (glLines, mappings, deptMappings, months) → { actuals, failures, collisions }.
 */
import { matchPayrollRole, resolvePayrollDept } from './inferIsMeta';
// ★ B4 — the ONE department-name slugifier (was inferIsMeta's near-copy slugifyDept).
import { deptSlug, groupIdOf } from './seatDepartments';
import { buildPayrollIsLineKey, PAYTYPES } from '../engines/payrollKeys';
import { IS_CATEGORY_DISPLAY_TO_SLUG } from '../engines/lineRoles';
import { matchISCategory } from './isCategoryMatch';

const r2 = n => Math.round(n * 100) / 100;
// matchPayrollRole role → the paytype the key uses. Commissions FOLD into bonuses for the per-dg
// path (§10.5); no separate commissions key. (hgl payroll rows are wages/bonuses/taxes/benefits —
// commissions come from the engine, not history — so payroll_commissions is defensive.)
const ROLE_TO_PAYTYPE = {
  payroll_wages: 'wages', payroll_bonuses: 'bonuses', payroll_commissions: 'bonuses',
  payroll_tax: 'payroll_taxes', payroll_benefits: 'benefits',
};
/* Commission ship 2c (Brian 10-09, Q4 A / R15): the STORED pay type of a "Handled by Payroll" row
 * (gl_line_mappings.payroll_paytype, the Step-3 answer) WINS over the name rule above; NULL = never answered = the name rule,
 * exactly as before. So a row stored as 'commissions' writes its history to the group's *_commissions key -- the key the
 * builder's Commissions line and the engine's forecast use (commissionLineKeys below): ONE row carries history then forecast. */
const storedPaytypeOf = glm => (glm && glm.handled_by === 'payroll' && PAYTYPES.includes(glm.payroll_paytype)) ? glm.payroll_paytype : null;

export function derivePerDgPayrollActuals(glLines, mappings, deptMappings, months) {
  const mlookup = new Map();
  for (const m of (mappings || [])) mlookup.set(`${m.statement_type}|${m.gl_name}|${m.gl_category || ''}`, m);
  const monthSet = new Set(months || []);
  const depts = deptMappings || [];

  // Group hgl IS payroll rows by (gl_name × gl_category), summing over HISTORICAL months only.
  const groups = new Map();
  for (const row of (glLines || [])) {
    if (row.statement_type !== 'IS') continue;
    if (!matchPayrollRole(row.gl_name) && !storedPaytypeOf(mlookup.get(`IS|${row.gl_name}|${row.gl_category || ''}`))) continue;   // 2c: or a stored pay type
    if (monthSet.size && !monthSet.has(row.month)) continue;   // historical window only
    /* ★ display_group is part of the group key. The canonical template puts the DEPARTMENT
     * in its own column (client_success / rev_ops / data_science / sales_mktg) and the
     * SECTION in is_category → gl_category, so grouping on (gl_name, gl_category) alone
     * re-merges the four S&M departments into one row and the per-dg split is lost before
     * a key is ever built. Legacy tenants carry display_group '' and are unaffected. */
    const k = `${row.gl_name}|${row.gl_category || ''}|${row.display_group || ''}`;
    if (!groups.has(k)) groups.set(k, { gl_name: row.gl_name, gl_category: row.gl_category || '', display_group: row.display_group || '', amt: {} });
    const g = groups.get(k);
    g.amt[row.month] = r2((g.amt[row.month] || 0) + (parseFloat(row.amount) || 0));
  }

  const actuals = {};             // key → { month: amount }
  const commissionKeys = new Set();   // 2c: the *_commissions keys a stored 'commissions' row writes
  const keyOrigin = new Map();    // key → Set(gl_category)  (D5 collision detection)
  const failures = [];            // { gl_name, gl_category, reason, amount }
  // W-022: each refusal carries the history it leaves unwritten (its months, summed) so Step 5c can
  // name the dollars on screen. Additive: `actuals` and `collisions` are unchanged.
  const fail = (g, reason) => failures.push({ gl_name: g.gl_name, gl_category: g.gl_category, reason,
    amount: r2(Object.values(g.amt).reduce((s, v) => s + v, 0)) });

  for (const g of groups.values()) {
    const glm = mlookup.get(`IS|${g.gl_name}|${g.gl_category}`);
    // ★ SKIP rows buildFinancialsPayload already writes (mapped catch-alls: is_line_key set, not Skip).
    if (glm && glm.is_line_key && glm.is_category && glm.is_category !== 'Skip') continue;

    const rulePaytype = ROLE_TO_PAYTYPE[matchPayrollRole(g.gl_name)] || null;   // today's rule (commission folds into bonuses)
    const paytype = storedPaytypeOf(glm) || rulePaytype;                         // 2c (R15): the stored answer wins
    const res = resolvePayrollDept(g.gl_category, depts);
    if (res.catAmbiguous) { fail(g, `matched departments disagree on is_category (${res.matched.join(', ')})`); continue; }
    if (res.dgAmbiguous)  { fail(g, `matched departments disagree on display_group (${res.matched.join(', ')})`); continue; }

    // D4 cat_slug: (1) the matched department's is_category → slug; (2) matchISCategory(gl_category); (3) fail loud.
    const catDisplay = res.is_category || matchISCategory(g.gl_category);
    if (!catDisplay) { fail(g, 'no department token-match and matchISCategory is blank — cannot resolve category'); continue; }
    const catSlug = IS_CATEGORY_DISPLAY_TO_SLUG[catDisplay];
    if (!catSlug) { fail(g, `category "${catDisplay}" is not a payroll section (Other/Skip have no slug)`); continue; }

    // Is this category MULTI-dg? (≥2 distinct display_groups among its departments.) If so and this
    // row's dg did not resolve, we CANNOT split it → FAIL LOUD. Single-dg → collapse (dg=null OK).
    const catDgs = new Set(depts.filter(d => d.is_category === catDisplay)
      .map(d => d.display_group || deptSlug(d.department_name)));
    const multiDg = catDgs.size >= 2;
    /* ★ THE ROW'S OWN display_group WINS over the gl_category token-match.
     *
     * resolvePayrollDept infers the department by matching gl_category against
     * department_mappings — which works for a workbook whose section headers ARE the
     * departments ("Client Success, Support & Imp"), but returns nothing for a canonical
     * template, where gl_category is the SECTION ("S&M") and the department sits in its
     * own display_group column. Without this the S&M rows hit the multiDg fail-loud below
     * and no per-department history is written at all.
     *
     * Legacy rows carry '' → falls through to the token-match → byte-identical behaviour
     * for cid=1/2/3. An explicit column beats an inference when both are available. */
    /* 2026-10-28: the row's group TEXT ("Cost of Revenue") means the department group deptSlug(text) -- the ONE rule,
     * utils/seatDepartments groupIdOf. Without it the key read sm_Sales_wages / cogs_Cost of Revenue_wages and matched no
     * statement line (diffs/step5-group-names-check.txt). A group stored as a slug is unchanged. */
    const dg = groupIdOf(g.display_group) || res.display_group;   // '' / null → fall back to the token-match
    if (multiDg && dg == null) { fail(g, `category "${catDisplay}" is multi-department but this row's gl_category resolves to no display_group — cannot split`); continue; }

    const key = buildPayrollIsLineKey(catSlug, dg, paytype);
    if (!keyOrigin.has(key)) keyOrigin.set(key, new Set());
    keyOrigin.get(key).add(g.gl_category);
    actuals[key] = actuals[key] || {};
    for (const [m, v] of Object.entries(g.amt)) actuals[key][m] = r2((actuals[key][m] || 0) + v);
    if (paytype === 'commissions') commissionKeys.add(key);
    /* 2c: a stored answer that MOVES this row off its rule key (Commissions off *_bonuses): the rule key is still written --
     * 0 for this row's months, plus any other row's own amounts -- so a re-run OVERWRITES the history the old rule put there
     * instead of leaving it beside the new key (the per-key merge would otherwise keep it: a double count). */
    if (rulePaytype && rulePaytype !== paytype) {
      const ruleKey = buildPayrollIsLineKey(catSlug, dg, rulePaytype);
      actuals[ruleKey] = actuals[ruleKey] || {};
      for (const m of Object.keys(g.amt)) if (!(m in actuals[ruleKey])) actuals[ruleKey][m] = 0;
    }
  }

  // D5: two DISTINCT gl_categories that produced the same key (silently summed) → report.
  const collisions = [];
  for (const [key, cats] of keyOrigin) if (cats.size > 1) collisions.push({ key, gl_categories: [...cats] });

  return { actuals, failures, collisions, commissionKeys: [...commissionKeys].sort() };
}

/* Commission ship 2c -- THE ONE QUALIFYING SET (Brian 10-09, Q4 A / N4 (a)): the *_commissions IS keys of the
 * (category, department group) pairs whose uploaded income statement has a payroll row STORED as 'commissions'. It is exactly
 * the set of keys the back-fill above writes for those rows -- the same row -> (category, group) resolution -- so the history
 * key, the builder's leaf (buildISLines, via moduleContext.commissionLineKeys) and the engine's forecast key (computePayroll)
 * cannot disagree. Every other group keeps commission folded into Bonuses. No stored 'commissions' answer -> []. */
export function commissionLineKeys(glLines, mappings, deptMappings) {
  if (!(mappings || []).some(m => m && m.handled_by === 'payroll' && m.payroll_paytype === 'commissions')) return [];
  return derivePerDgPayrollActuals(glLines, mappings, deptMappings, null).commissionKeys;
}
